import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import {
	AlertTriangle,
	BotOff,
	ChevronDown,
	ChevronRight,
	Loader2,
	Search,
	Send,
	Workflow,
} from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import PreviaDoFluxo from "../PreviaDoFluxo";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { cn } from "../../lib/utils";
import {
	primeiraFalaDoFluxo,
	resumoDaPrimeiraFala,
} from "../../helpers/previaDoFluxo";

// Situação de cada execução, em português e com a cor só onde pede atenção.
const SITUACAO = {
	pending: { rotulo: "Na fila para começar", classe: "bg-muted text-foreground" },
	running: { rotulo: "Enviando agora", classe: "bg-primary/10 text-primary" },
	waiting: {
		rotulo: "Esperando a resposta",
		classe: "bg-primary/10 text-primary",
	},
	completed: { rotulo: "Concluído", classe: "bg-muted text-foreground" },
	failed: {
		rotulo: "Falhou",
		classe: "bg-destructive/10 text-destructive",
	},
	cancelled: { rotulo: "Interrompido", classe: "bg-muted text-foreground" },
};

const quando = (data) => {
	try {
		return format(parseISO(data), "dd/MM/yyyy, HH:mm");
	} catch {
		return "";
	}
};

// Quem pôs o fluxo nesta conversa, numa frase.
const origemDaExecucao = (execucao) => {
	if (execucao.origem === "atendente") {
		return `enviado por ${execucao.enviadoPor || "alguém da equipe"}`;
	}

	if (execucao.origem === "campanha") {
		return `pela campanha “${execucao.campanha || "sem nome"}”`;
	}

	return "começou sozinho, por mensagem do cliente";
};

// Mandar um fluxo para ESTA conversa, agora.
//
// Pensado para quem nunca montou um robô: a lista mostra todos os fluxos pelo
// nome, um clique escolhe, os outros somem e fica só a pergunta "enviar
// mesmo?" — com a primeira mensagem na tela para conferir antes.
const EnviarFluxoModal = ({ open, onClose, ticket }) => {
	const [fluxos, setFluxos] = useState([]);
	const [situacao, setSituacao] = useState({ emAndamento: null, recentes: [] });
	const [janela, setJanela] = useState({ aplica: false, aberta: true });
	const [carregando, setCarregando] = useState(true);
	const [busca, setBusca] = useState("");
	const [escolhido, setEscolhido] = useState(null);
	const [grupoConfirmado, setGrupoConfirmado] = useState(false);
	const [historicoAberto, setHistoricoAberto] = useState(false);
	const [enviando, setEnviando] = useState(false);
	const [parando, setParando] = useState(false);

	const ticketId = ticket?.id;
	const nomeDoContato = ticket?.contact?.name || "";
	const encerrado = ticket?.status === "closed";

	const carregarSituacao = useCallback(async () => {
		if (!ticketId) return;

		try {
			const { data } = await api.get(`/tickets/${ticketId}/flow`);
			setSituacao({
				emAndamento: data?.emAndamento || null,
				recentes: data?.recentes || [],
			});
		} catch (err) {
			toastError(err);
		}
	}, [ticketId]);

	// Quem abre monta este modal só enquanto ele está aberto: cada abertura
	// começa do zero, sem a escolha da vez anterior marcada esperando um
	// clique distraído.
	useEffect(() => {
		if (!open || !ticketId) return;

		(async () => {
			try {
				const { data } = await api.get("/flows");
				setFluxos(Array.isArray(data) ? data : data?.flows || []);
			} catch (err) {
				toastError(err);
			}

			await carregarSituacao();

			try {
				const { data } = await api.get(`/meta/templates/window/${ticketId}`);
				setJanela(data || { aplica: false, aberta: true });
			} catch {
				// Sem resposta, nada de aviso: o limite de 24 h só existe no
				// WhatsApp oficial, e bloquear por falha de consulta atrapalharia
				// os outros canais.
				setJanela({ aplica: false, aberta: true });
			}

			setCarregando(false);
		})();
	}, [open, ticketId, carregarSituacao]);

	const filtrados = useMemo(() => {
		const termo = busca.trim().toLowerCase();

		// Ligados primeiro: são os que a empresa usa no dia a dia.
		const ordenados = [...fluxos].sort(
			(a, b) =>
				Number(Boolean(b.isActive)) - Number(Boolean(a.isActive)) ||
				String(a.name).localeCompare(String(b.name), "pt-BR"),
		);

		return termo
			? ordenados.filter((f) => String(f.name).toLowerCase().includes(termo))
			: ordenados;
	}, [fluxos, busca]);

	const emAndamento = situacao.emAndamento;
	const anteriores = situacao.recentes.filter(
		(execucao) => execucao.id !== emAndamento?.id,
	);

	const previaDoEscolhido = escolhido
		? primeiraFalaDoFluxo(escolhido, nomeDoContato)
		: null;

	const podeEnviar =
		Boolean(escolhido) &&
		!encerrado &&
		previaDoEscolhido?.tipo !== "vazio" &&
		(!ticket?.isGroup || grupoConfirmado) &&
		!enviando;

	const enviar = async () => {
		if (!podeEnviar) return;

		setEnviando(true);
		try {
			await api.post(`/tickets/${ticketId}/flow`, {
				flowId: escolhido.id,
				confirmarGrupo: Boolean(ticket?.isGroup && grupoConfirmado),
			});

			toast.success(
				`Fluxo “${escolhido.name}” enviado. As mensagens aparecem na conversa em instantes.`,
			);
			onClose();
		} catch (err) {
			toastError(err);
		}
		setEnviando(false);
	};

	const parar = async () => {
		setParando(true);
		try {
			await api.delete(`/tickets/${ticketId}/flow`);
			toast.success("Fluxo parado. Ele não fala mais nesta conversa.");
			await carregarSituacao();
		} catch (err) {
			toastError(err);
		}
		setParando(false);
	};

	return (
		<Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{escolhido ? "Enviar este fluxo?" : "Enviar fluxo"}
					</DialogTitle>
					<DialogDescription>
						{escolhido
							? `Ele começa agora${
									nomeDoContato ? ` com ${nomeDoContato}` : ""
								}, como se a pessoa tivesse escrito.`
							: "Escolha o fluxo que esta conversa vai receber."}
					</DialogDescription>
				</DialogHeader>

				{carregando ? (
					<div className="flex items-center justify-center py-10">
						<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
					</div>
				) : (
					<div className="space-y-4">
						{/* O que já está falando aqui. Mandar outro por cima interrompe
						    este — quem vai enviar precisa saber antes. */}
						{emAndamento && (
							<div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-3">
								<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
									<Workflow className="h-4 w-4 text-primary" />
								</span>
								<div className="min-w-0 flex-1">
									<p className="truncate text-sm font-medium">
										“{emAndamento.fluxo}” está nesta conversa
									</p>
									<p className="text-xs text-muted-foreground">
										{SITUACAO[emAndamento.status]?.rotulo || emAndamento.status}
										{" · "}
										{origemDaExecucao(emAndamento)}
									</p>
								</div>
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="h-8"
									disabled={parando}
									onClick={parar}
								>
									{parando && <Loader2 className="h-4 w-4 animate-spin" />}
									Parar este fluxo
								</Button>
							</div>
						)}

						{encerrado && (
							<p className="flex gap-2 rounded-lg bg-muted p-3 text-sm">
								<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
								Esta conversa está encerrada. Reabra-a para enviar um fluxo.
							</p>
						)}

						{!escolhido && (
							<>
								{fluxos.length === 0 ? (
									<div className="rounded-xl border border-dashed p-6 text-center">
										<Workflow className="mx-auto h-6 w-6 text-muted-foreground" />
										<p className="mt-2 text-sm font-medium">
											Nenhum fluxo criado ainda
										</p>
										<p className="mt-1 text-xs text-muted-foreground">
											Monte o primeiro em Fluxos. Ele aparece aqui para enviar
											a qualquer conversa.
										</p>
										<Button asChild size="sm" variant="secondary" className="mt-3">
											<Link to="/flows" onClick={onClose}>
												Ir para Fluxos
											</Link>
										</Button>
									</div>
								) : (
									<>
										{fluxos.length > 6 && (
											<div className="relative">
												<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input
													value={busca}
													onChange={(e) => setBusca(e.target.value)}
													placeholder="Procurar fluxo pelo nome"
													aria-label="Procurar fluxo pelo nome"
													className="pl-9"
												/>
											</div>
										)}

										<ul className="space-y-2" aria-label="Fluxos">
											{filtrados.map((fluxo) => (
												<li key={fluxo.id}>
													<button
														type="button"
														disabled={encerrado}
														onClick={() => setEscolhido(fluxo)}
														className="flex w-full items-center gap-3 rounded-xl border bg-card p-3 text-left transition-colors hover:border-primary/50 hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
													>
														<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
															<Workflow className="h-4 w-4 text-muted-foreground" />
														</span>
														<span className="min-w-0 flex-1">
															<span className="flex items-center gap-2">
																<span className="truncate text-sm font-medium">
																	{fluxo.name}
																</span>
																{!fluxo.isActive && (
																	<span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
																		Desligado
																	</span>
																)}
															</span>
															<span className="block truncate text-xs text-muted-foreground">
																{resumoDaPrimeiraFala(fluxo)}
															</span>
														</span>
														<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
													</button>
												</li>
											))}
											{filtrados.length === 0 && (
												<li className="rounded-xl border border-dashed p-4 text-center text-sm text-muted-foreground">
													Nenhum fluxo com “{busca}” no nome.
												</li>
											)}
										</ul>
									</>
								)}

								{anteriores.length > 0 && (
									<div className="rounded-xl border">
										<button
											type="button"
											onClick={() => setHistoricoAberto((v) => !v)}
											className="flex w-full items-center gap-2 p-3 text-left text-sm transition-colors hover:bg-muted/50"
										>
											<span className="flex-1 font-medium">
												Já passaram por esta conversa
											</span>
											<span className="text-xs tabular-nums text-muted-foreground">
												{anteriores.length}{" "}
												{anteriores.length > 1 ? "fluxos" : "fluxo"}
											</span>
											<ChevronDown
												className={cn(
													"h-4 w-4 text-muted-foreground transition-transform",
													historicoAberto && "rotate-180",
												)}
											/>
										</button>
										{historicoAberto && (
											<ul className="space-y-2 border-t p-3">
												{anteriores.map((execucao) => (
													<li key={execucao.id} className="space-y-1 text-sm">
														<div className="flex items-center gap-2">
															<span
																className={cn(
																	"shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium",
																	SITUACAO[execucao.status]?.classe ||
																		"bg-muted text-foreground",
																)}
															>
																{SITUACAO[execucao.status]?.rotulo ||
																	execucao.status}
															</span>
															<span className="min-w-0 flex-1 truncate font-medium">
																{execucao.fluxo}
															</span>
															<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
																{quando(execucao.criadoEm)}
															</span>
														</div>
														<p className="text-xs text-muted-foreground">
															{origemDaExecucao(execucao)}
															{/* O motivo só importa quando algo deu errado. */}
															{["failed", "cancelled"].includes(execucao.status) &&
																execucao.detalhe &&
																` — ${execucao.detalhe}`}
														</p>
													</li>
												))}
											</ul>
										)}
									</div>
								)}
							</>
						)}

						{escolhido && (
							<>
								{/* Só o escolhido fica na tela. "Trocar" volta para a lista. */}
								<div className="flex items-center gap-3 rounded-xl border border-primary/60 bg-primary/5 p-3">
									<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
										<Workflow className="h-4 w-4 text-primary" />
									</span>
									<span className="min-w-0 flex-1 truncate text-sm font-medium">
										{escolhido.name}
									</span>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="h-8"
										disabled={enviando}
										onClick={() => {
											setEscolhido(null);
											setGrupoConfirmado(false);
										}}
									>
										Trocar
									</Button>
								</div>

								<PreviaDoFluxo fluxo={escolhido} nome={nomeDoContato} />

								{ticket?.isGroup && (
									<div className="space-y-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
										<p className="flex gap-2">
											<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
											Esta conversa é um grupo: tudo o que o fluxo disser aparece
											para todos que estão nele.
										</p>
										<label className="flex cursor-pointer items-center gap-2 font-medium">
											<Checkbox
												checked={grupoConfirmado}
												onCheckedChange={(v) => setGrupoConfirmado(v === true)}
											/>
											Entendi, pode enviar no grupo
										</label>
									</div>
								)}

								{janela.aplica && !janela.aberta && (
									<p className="flex gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
										<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
										A pessoa não escreve há mais de 24 horas. No WhatsApp
										oficial só um modelo aprovado passa nesse caso, e o fluxo
										vai falhar até ela responder.
									</p>
								)}

								{emAndamento && (
									<p className="flex gap-2 text-xs leading-relaxed text-muted-foreground">
										<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
										“{emAndamento.fluxo}”, que está nesta conversa agora, vai
										ser interrompido.
									</p>
								)}

								{ticket?.botPaused && (
									<p className="flex gap-2 text-xs leading-relaxed text-muted-foreground">
										<BotOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
										O robô está pausado aqui, mas este fluxo roda mesmo assim:
										foi você quem mandou.
									</p>
								)}

								{!escolhido.isActive && (
									<p className="text-xs leading-relaxed text-muted-foreground">
										Este fluxo está desligado: não responde sozinho, mas pode ser
										enviado assim, à mão.
									</p>
								)}

								<p className="text-xs leading-relaxed text-muted-foreground">
									Vai uma vez só. Se a pessoa escrever de novo depois, o fluxo
									não recomeça — só as respostas a menus e perguntas continuam
									o fluxo.
								</p>
							</>
						)}
					</div>
				)}

				<DialogFooter className="gap-2">
					{escolhido ? (
						<>
							<Button
								type="button"
								variant="outline"
								disabled={enviando}
								onClick={() => {
									setEscolhido(null);
									setGrupoConfirmado(false);
								}}
							>
								Voltar
							</Button>
							<Button type="button" disabled={!podeEnviar} onClick={enviar}>
								{enviando ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Send className="h-4 w-4" />
								)}
								Sim, enviar agora
							</Button>
						</>
					) : (
						<Button type="button" variant="outline" onClick={onClose}>
							Fechar
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default EnviarFluxoModal;
